import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/ui/sonner";
import {
  applyRequest,
  fetchSession,
  googleLoginRequest,
  otpSend,
  otpVerify,
  loginRequest,
  logoutRequest,
  roleLabel,
  setRefreshUnauthorizedHandler,
  startDevAccess,
} from "@/lib/api";

// Dev-only affordance (the role picker) is compiled out of the production
// build — CRA sets NODE_ENV=production for `yarn build`.
export const DEV_TOOLS_ENABLED = process.env.NODE_ENV !== "production";

export const useAuth = () => {
  // session is null when signed out and carries server-computed gate flags when signed in.
  const [session, setSession] = useState(null);
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const [authReady, setAuthReady] = useState(false);
  const [phoneDeferred, setPhoneDeferred] = useState(false);
  const bootstrapped = useRef(false);
  const queryClient = useQueryClient();

  const replaceSession = useCallback((nextSession) => {
    // Customer data is cached across the member bundle, site selector,
    // monitoring, and support query families. A credential/account change
    // must never render the previous account's cached state.
    queryClient.clear();
    setSession(nextSession);
  }, [queryClient]);

  const startSession = useCallback((nextSession) => {
    if (!nextSession) return;
    setPhoneDeferred(false);
    replaceSession(nextSession);
    setSessionEpoch((epoch) => epoch + 1);
  }, [replaceSession]);

  const authSessionFromLogin = useCallback((payload) => {
    if (!payload) return null;
    return {
      role: payload.role,
      memberId: payload.member_id,
      needsPhone: payload.needs_phone === true,
      needsPassword: payload.needs_password === true,
    };
  }, []);

  useEffect(
    () => setRefreshUnauthorizedHandler(() => replaceSession(null)),
    [replaceSession],
  );

  // Restore an existing session (real login or a prior dev-access cookie).
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    (async () => {
      setPhoneDeferred(false);
      try {
        const me = await fetchSession();
        replaceSession({
          role: me.role,
          memberId: me.member_id,
          needsPhone: me.needs_phone === true,
          needsPassword: me.needs_password === true,
        });
      } catch {
        replaceSession(null);
      } finally {
        setAuthReady(true);
      }
    })();
  }, [replaceSession]);

  const login = useCallback(async (identifier, password) => {
    const data = await loginRequest(identifier, password);
    startSession(authSessionFromLogin(data));
    return data;
  }, [startSession, authSessionFromLogin]);

  const sendOtpCode = useCallback(async (identifier, options) => {
    return otpSend(identifier, options);
  }, []);

  const verifyOtpCode = useCallback(async (identifier, otp, options) => {
    const data = await otpVerify(identifier, otp, options);
    if (data?.role) {
      startSession(authSessionFromLogin(data));
    }
    return data;
  }, [startSession, authSessionFromLogin]);

  // Every applicant registers as a Customer, is auto-approved, and is logged
  // straight in; they set a password in-app. An application that still needs
  // admin review starts no session.
  const apply = useCallback(async (payload) => {
    const data = await applyRequest(payload);
    if (data.status === "approved") {
      startSession(authSessionFromLogin(data));
    }
    return data;
  }, [startSession, authSessionFromLogin]);

  // Sign in with Google. New users are auto-created as customers; existing
  // accounts log into their real role. The App redirect effect handles navigation.
  const googleLogin = useCallback(async (credential) => {
    const data = await googleLoginRequest(credential);
    startSession(authSessionFromLogin(data));
    return data;
  }, [startSession, authSessionFromLogin]);

  const markPasswordSet = useCallback(
    () => setSession((prev) => (prev ? { ...prev, needsPassword: false } : prev)),
    [],
  );

  const markPhoneSet = useCallback(
    () => setSession((prev) => (prev ? { ...prev, needsPhone: false } : prev)),
    [],
  );

  const deferPhone = useCallback(() => setPhoneDeferred(true), []);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } catch {
      /* clearing local state below is what matters */
    }
    setPhoneDeferred(false);
    replaceSession(null);
  }, [replaceSession]);

  // Dev-only: open the app as any role without logging in.
  const startDevRole = useCallback(async (nextRole) => {
    try {
      const data = await startDevAccess(nextRole);
      startSession((data?.role && authSessionFromLogin(data)) || {
        role: nextRole,
        memberId: null,
        needsPhone: false,
        needsPassword: false,
      });
      toast.success(`Viewing ${roleLabel(nextRole)}`);
    } catch (error) {
      toast.error("Could not start developer preview");
      throw error;
    }
  }, [startSession, authSessionFromLogin]);

  return {
    session,
    sessionEpoch,
    authReady,
    role: session?.role,
    needsPhone: session?.needsPhone,
    needsPassword: session?.needsPassword,
    phoneDeferred,
    markPhoneSet,
    markPasswordSet,
    deferPhone,
    login,
    googleLogin,
    apply,
    logout,
    startDevRole,
    switchRole: startDevRole,
    sendOtpCode,
    verifyOtpCode,
  };
};
